import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface AuthUser {
  org_id: string;
  account_id: string;
  user_id: string;
  role: string;
  name: string;
  email: string;
}

/** 'takeover' = super-admin support session; 'manage_as' = an org's Engage
 *  admin working on behalf of one of their consultants. */
export type ImpersonationKind = 'manage_as' | 'takeover';

/**
 * Session persistence — Section 7.1 of the architecture doc says the
 * Engage JWT lives in memory + an httpOnly cookie. We don't have the
 * cookie path yet, so for now we mirror auth state to **sessionStorage**:
 *
 *   - dies on tab close (matches "session" semantics)
 *   - never written to localStorage
 *   - includes the JT access token captured at exchange so manual sync
 *     and other user-initiated JT-touching flows don't re-prompt
 *
 * The token watchdog (useJtTokenWatchdog) handles definitive JT-token
 * expiry by calling clearAuth(), which also wipes sessionStorage via
 * persist's storage adapter. Engage-JWT expiry is handled by the global
 * axios 401 interceptor in api/client.ts.
 *
 * Long-term path: per-org JT_API_KEY (Section 11.1) replaces the user
 * token for server-side sync entirely, and the Engage JWT moves to an
 * httpOnly cookie. Both reduce the surface area persist() touches here.
 */

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  /** True when the user is on the super-admin allowlist (server-checked at login).
   *  Non-authoritative — the server re-checks on every /system/* request. */
  isSuperAdmin: boolean;
  /** True when the current session is any impersonation JWT (takeover or
   *  Manage as). Kept for back-compat — same as impersonationKind !== null. */
  isTakeover: boolean;
  /** Email of the person doing the impersonating. */
  takeoverBy: string | null;
  /** Which kind of impersonation this session is, or null for a normal sign-in. */
  impersonationKind: ImpersonationKind | null;
  /** ISO expiry of the impersonation token (banner countdown), when known. */
  impersonationExpiresAt: string | null;
  /** Original JT access token used at login. Stashed for user-initiated
   *  flows that need to authenticate against JT directly (manual sync,
   *  future "refresh this candidate" buttons). */
  jtToken: string | null;
  setAuth: (
    token: string,
    user: AuthUser,
    opts?: {
      isSuperAdmin?: boolean;
      /** Legacy flag — true without impersonationKind means a takeover. */
      isTakeover?: boolean;
      takeoverBy?: string | null;
      jtToken?: string | null;
      impersonationKind?: ImpersonationKind | null;
      impersonationExpiresAt?: string | null;
    }
  ) => void;
  setJtToken: (jtToken: string | null) => void;
  clearAuth: () => void;
  isAuthenticated: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      isSuperAdmin: false,
      isTakeover: false,
      takeoverBy: null,
      impersonationKind: null,
      impersonationExpiresAt: null,
      jtToken: null,
      setAuth: (token, user, opts = {}) => set((prev) => {
        const impersonationKind = opts.impersonationKind ?? (opts.isTakeover ? 'takeover' : null);
        return {
          token,
          user,
          isSuperAdmin: !!opts.isSuperAdmin,
          isTakeover: impersonationKind !== null,
          takeoverBy: opts.takeoverBy ?? null,
          impersonationKind,
          impersonationExpiresAt: impersonationKind ? opts.impersonationExpiresAt ?? null : null,
          // Only overwrite the in-memory jtToken when the caller explicitly
          // passed one — silent re-auths (token refresh flows, future code
          // paths) shouldn't wipe the stored JT token.
          jtToken: opts.jtToken !== undefined ? opts.jtToken : prev.jtToken,
        };
      }),
      setJtToken: (jtToken) => set({ jtToken }),
      clearAuth: () => set({
        token: null,
        user: null,
        isSuperAdmin: false,
        isTakeover: false,
        takeoverBy: null,
        impersonationKind: null,
        impersonationExpiresAt: null,
        jtToken: null,
      }),
      isAuthenticated: () => !!get().token,
    }),
    {
      name: 'engage-auth',
      storage: createJSONStorage(() => sessionStorage),
      // Persist data fields only — functions can't be JSON-serialised.
      // Takeover flags ARE persisted so a refresh of a takeover tab keeps
      // the warning banner visible. Closing the tab ends the session
      // (sessionStorage scope); the server is authoritative on the
      // takeover JWT's validity (1h expiry, separate signing secret).
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        isSuperAdmin: state.isSuperAdmin,
        isTakeover: state.isTakeover,
        takeoverBy: state.takeoverBy,
        impersonationKind: state.impersonationKind,
        impersonationExpiresAt: state.impersonationExpiresAt,
        jtToken: state.jtToken,
      }),
    }
  )
);
